import { ImportButton } from '@components/Buttons/ImportButton';
import { GridItem } from '@components/GridItem';
import { HorizontalGrid } from '@components/HorizontalGrid';
import { Main } from '@components/Main';
import { PageContent } from '@components/PageContent';
import { PageTitle, PageTitleText } from '@components/PageTitle';
import { useEnumCombo } from '@hooks/useCombo';
import { useData } from '@hooks/useData';
import { useEffectOnce } from '@hooks/useEffectOnce';
import { useField } from '@hooks/useField';
import useLoading from '@hooks/useLoading';
import { useMenu } from '@hooks/useMenu';
import useMessage from '@hooks/useMessage';
import { useModalWindow } from '@hooks/useModalWindow';
import { EnumSortDirection } from '@hooks/useRequest';
import { AtividadeModal } from '@mod/Atividade/AtividadeModal';
import { EnumExcedenteStatus, IExcedente } from '@model/Excedente';
import { IExcedenteFilter, useExcedenteService } from '@services/ExcedenteService';
import React from 'react';
import { ExcedenteCancelarModal } from './ExcedenteCancelarModal';
import { ExcedenteFilter } from './ExcedenteFilter';
import { ExcedenteImportarModal } from './ExcedenteImportarModal';
import { ExcedenteMenu } from './ExcedenteMenu';
import { ExcedenteTable } from './ExcedenteTable';

export type ExcedenteProps = {
}

export function Excedente (props: ExcedenteProps) {
    const data = useData<IExcedente, IExcedenteFilter>({
        filter: {
            numero: useField(),
            competencia: useField(),
            excedenteStatus: useField(EnumExcedenteStatus.None)
        },

        sortField: {
            name: "excedenteId",
            direction: EnumSortDirection.Desc
        }
    })

    const loading = useLoading();
    const message = useMessage();
    const excedenteService = useExcedenteService();

    const excedenteStatusCombo = useEnumCombo<EnumExcedenteStatus>(excedenteService.listarStatusAtivos);

    const menu = useMenu();
    const importarModal = useModalWindow();
    const cancelarModal = useModalWindow();
    const atividadeModal = useModalWindow();

    React.useEffect(() => {
        if (!loading.isLoading) {
            loading.start();
            excedenteService.listar(data.requestedData)
                .then(data.handleResultResponse)
                .catch(message.showException)
                .finally(loading.stop);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.requestedData]);

    useEffectOnce(() => {
        excedenteStatusCombo.loadOptions();
    });

    return (
        <React.Fragment>
            <Main>
                <PageTitle>
                    <HorizontalGrid>
                        <GridItem>
                            <PageTitleText text="Excedentes" />
                        </GridItem>
                        <GridItem floatRight>
                            <ImportButton onClick={importarModal.open} />
                        </GridItem>
                    </HorizontalGrid>
                </PageTitle>

                <PageContent>
                    <ExcedenteFilter
                        filter={data.filter}
                        isLoading={loading.isLoading}
                        excedenteStatusCombo={excedenteStatusCombo}
                    />
                    
                    <ExcedenteTable 
                        data={data}
                        isLoading={loading.isLoading}
                        menu={menu}
                    />
                </PageContent>
            </Main>

            <ExcedenteImportarModal 
                modal={importarModal}
                onExcedenteImportado={data.filter.handleFilterRequest}
            />

            {
                data.focusedRow && (
                    <React.Fragment>
                        <ExcedenteMenu
                            menu={menu}
                            cancelarModal={cancelarModal}
                            atividadeModal={atividadeModal}
                        />

                        <ExcedenteCancelarModal 
                            excedente={data.focusedRow}
                            modal={cancelarModal}
                            onExcedenteCancelado={data.handleUpdateFocusedRow}
                        />

                        <AtividadeModal 
                            modal={atividadeModal}
                            entidade='excedente'
                            chaves={{
                                excedenteId: data.focusedRow.excedenteId
                            }}
                        />
                    </React.Fragment>
                )
            }
        </React.Fragment>
    )
}